import { H2 } from '@triumph/design-system';
import { AccordionTab } from 'primereact/accordion';

import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import { WatchlistMatchDto } from '@/components/shared/api/endpoints/applicant';
import { useApplicantWatchlistMatchesQuery } from '@/components/shared/api/queries/applicant';
import { NoMarginParagraph } from '@/components/shared/components/common.StyledComponents';
import { TabDataState } from '@/components/shared/components/TabDataState';

import {
    AccordionHeaderContent,
    AccordionIcon,
    ClearChip,
    EntityBlock,
    EntityHeader,
    ListTitle,
    MatchCountBadge,
    MatchDetailRow,
    MatchDetailTable,
    MatchFieldLabel,
    ServicePill,
    StatusBadge,
    WarningChip,
    WatchlistAccordion,
    WatchlistWrapper,
} from './WatchlistMatches.StyledComponents';

const WATCHLIST_LOAD_ERROR_MESSAGE = 'Could not load watchlist data.';
const WATCHLIST_EMPTY_MESSAGE = 'No watchlist data found for this applicant.';

const renderListings = (match: WatchlistMatchDto, accordionKey: string) => {
    if (match.listings.length === 0) {
        return (
            <NoMarginParagraph style={{ fontSize: 12, color: '#94a3b8' }}>
                No listing details available.
            </NoMarginParagraph>
        );
    }

    return match.listings.map((listing, i) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key -- read-only API rows with no id
        <MatchDetailTable key={`${accordionKey}-table-${i}`}>
            {listing.source ? (
                <MatchDetailRow>
                    <MatchFieldLabel>source:</MatchFieldLabel>
                    <span>{listing.source}</span>
                </MatchDetailRow>
            ) : null}
            {listing.matchType ? (
                <MatchDetailRow>
                    <MatchFieldLabel>match type:</MatchFieldLabel>
                    <span>{listing.matchType}</span>
                </MatchDetailRow>
            ) : null}
            {listing.details.map((detail, j) => (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <MatchDetailRow key={`${accordionKey}-table-${i}-detail-${j}`}>
                    <MatchFieldLabel>{detail.label}:</MatchFieldLabel>
                    <span>{detail.values.join(', ')}</span>
                </MatchDetailRow>
            ))}
        </MatchDetailTable>
    ));
};

const buildAccordionHeader = (match: WatchlistMatchDto) => (
    <AccordionHeaderContent>
        <ServicePill>{match.service ?? '—'}</ServicePill>
        <ListTitle hasMatches>{match.name ?? 'Unknown'}</ListTitle>
        {match.status ? <StatusBadge>{match.status}</StatusBadge> : null}
        {match.matchStrength != null ? (
            <MatchCountBadge>strength: {match.matchStrength}</MatchCountBadge>
        ) : null}
    </AccordionHeaderContent>
);

export const WatchlistMatches = () => {
    const { applicantId } = useApplicantContext();

    const {
        data: watchlists,
        isLoading,
        isLoadingError,
        refetch,
    } = useApplicantWatchlistMatchesQuery({
        applicantId: applicantId ?? '',
    });

    return (
        <TabDataState
            isLoading={isLoading}
            isLoadingError={isLoadingError}
            onRetry={() => refetch()}
            isEmpty={!watchlists || watchlists.length === 0}
            emptyMessage={WATCHLIST_EMPTY_MESSAGE}
            errorMessage={WATCHLIST_LOAD_ERROR_MESSAGE}
        >
            <WatchlistWrapper>
                {(watchlists ?? []).map(({ entityName, matches }, i) => (
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    <EntityBlock key={`watch-list-${i}`}>
                        <EntityHeader>
                            <H2>{entityName}</H2>
                            {matches.length > 0 ? (
                                <WarningChip>⚠ Watchlist Warning</WarningChip>
                            ) : (
                                <ClearChip>✓ No Watchlist Matches</ClearChip>
                            )}
                        </EntityHeader>
                        {matches.length > 0 ? (
                            <WatchlistAccordion
                                multiple
                                activeIndex={matches.map((_, j) => j)}
                                expandIcon={<AccordionIcon className="pi pi-angle-down" />}
                                collapseIcon={<AccordionIcon className="pi pi-angle-up" />}
                            >
                                {matches.map((match, j) => {
                                    const accordionKey = `watch-list-${i}-accordion-${j}`;
                                    return (
                                        <AccordionTab
                                            key={accordionKey}
                                            header={buildAccordionHeader(match)}
                                        >
                                            {renderListings(match, accordionKey)}
                                        </AccordionTab>
                                    );
                                })}
                            </WatchlistAccordion>
                        ) : (
                            <NoMarginParagraph style={{ fontSize: 12, color: '#94a3b8' }}>
                                No matches found.
                            </NoMarginParagraph>
                        )}
                    </EntityBlock>
                ))}
            </WatchlistWrapper>
        </TabDataState>
    );
};
